import { memo, MutableRefObject, useLayoutEffect, useRef, useState } from 'react'
import { compactFormatter } from '../../utils/formatterUSD.ts'
import { Color, rgbToHex, weightedMeanColor } from './diagram/color.ts'
import { Link } from './diagram/diagram.ts'
import { Point } from './diagram/point.ts'

export type LinkLabelData = {
    value: number,
    position: Point
}

export type DiagramLinkProps = {
    id: string,
    link: Link,
    height: number,
    width: number,
    x: number,
    y: number,
    path: string,
    amount: number,
    filter?: string,
    strokeColor?: string,
    labelOffset: number,
    backgroundColor: string,
    labels: string[],
    onClick: (link: Link) => void,
    onMouseOver: (link: Link) => void,
    onMouseLeave: () => void,
    opacity: number | string,
    valueProportion: number,
    colorFrom: Color,
    colorTo: Color
}
export const DiagramLinkLabel =
    memo(({
        id,
        link,
        width,
        height,
        x,
        y,
        path,
        amount,
        strokeColor,
        filter,
        backgroundColor,
        onClick,
        onMouseOver,
        onMouseLeave,
        opacity,
        valueProportion,
        colorFrom,
        colorTo
    }: DiagramLinkProps) => {
        const [rectWidth, setRectWidth] = useState<number>()
        const svgRef = useRef() as MutableRefObject<SVGSVGElement>
        const pathRef = useRef() as MutableRefObject<SVGPathElement>
        const [textElement, setTextElement] = useState<SVGTextElement | null>()
        const [nativeAmountTextElement, setNativeAmountTextElement] = useState<SVGTextElement | null>()

        const color = weightedMeanColor(colorFrom, 1 - valueProportion, colorTo, valueProportion)
        const hexColor = strokeColor || rgbToHex(color)
        // Tokens moved but none of them has a USD price: unknown, not worthless
        const isUnpriced = link.nativeValue?.some((item) => item.Amount > 0)
        const amountText = amount < 1 ? (amount > Number.MIN_VALUE ?
            '<$1' : (isUnpriced ? 'n/a' : '$0')) :
            compactFormatter('currency', 'USD').format(amount)

        const formatNativeAmounts = (
            nativeAmounts: Array<{ Token: string, Amount: number }>
        ): string => nativeAmounts.map(
            (item) => `${item.Token} ${compactFormatter('decimal').format(item.Amount)}`
        ).join(', ')
        const nativeAmountsString = formatNativeAmounts(link.nativeValue)

        useLayoutEffect(() => {
            if (!textElement || !nativeAmountTextElement) {
                return
            }

            const biggerText = textElement?.getBBox().width > (nativeAmountTextElement?.getBBox().width || NaN) ?
                textElement : nativeAmountTextElement

            setRectWidth(() => (biggerText?.getBBox().width || NaN) * 1.1)
        }, [nativeAmountTextElement, textElement, width])

        return <g>
            <path
                id={id}
                d={path}
                stroke='none'
                fill="none"
                ref={pathRef}
            />
            {pathRef.current && <svg
                x={x}
                y={y}
                viewBox={`0 0 ${width} ${height}`}
                onClick={() => onClick(link)}
                onMouseOver={() => onMouseOver(link)}
                onMouseLeave={onMouseLeave}
                height={height + 'px'}
                width={width + 'px'}
                overflow='visible'
                opacity={opacity}
                style={{ transform: 'translate(-50% -50%)' }}
                ref={svgRef}
            >
                <rect
                    stroke={hexColor}
                    fill={backgroundColor}
                    x={rectWidth ? (-rectWidth / 2) : '-50%'}
                    y={"-100%"}
                    style={{ filter }}
                    strokeLinecap='butt'
                    strokeLinejoin='bevel'
                    height={height * 2.3}
                    width={rectWidth}
                    rx={width / 2}
                    ry={width / 2}
                />
                <text
                    ref={(newRef) => setTextElement(() => newRef)}
                    x={rectWidth ? (-rectWidth / 2.2) : '-50%'}
                    style={{
                        overflow: 'hidden',
                        fontWeight: 600,
                        pointerEvents: 'none',
                    }}
                    fontSize={width}
                >
                    {amountText}
                </text>
                <text
                    ref={(newRef) => setNativeAmountTextElement(() => newRef)}
                    x={rectWidth ? (-rectWidth / 2.2) : '-50%'}
                    y={height}
                    style={{
                        pointerEvents: 'none',
                    }}
                    fontSize={width}
                >
                    {nativeAmountsString}
                </text>
            </svg>}
        </g>
    })
